import React from 'react'
import { useTheme } from '@mui/material/styles'
import { useParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { format, isToday, parseISO, startOfDay } from 'date-fns'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import Modal from '@/v2/components/common/Modal'
import Typography from '@/v2/components/common/Typography'
import SelectCalendar from '@/v2/components/common/SelectCalendar'
import ActionButton from '@/v2/components/common/ActionButton'
import FormErrorMessage from '@/v2/components/common/FormErrorMessage'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { useInvoiceActions } from '@/v2/hooks/useInvoiceActions'
import useDetectDevice from '@/v2/hooks/useDetectDevice'
import { Form } from '../styles'

const schema = yup.object({
  dueDate: yup
    .date()
    .typeError('Due date is required')
    .required('Due date is required')
    .test(
      'not-in-past',
      "Due date can't be in the past",
      value => !value || value >= startOfDay(new Date())
    ),
})

const EditInvoiceDueDate = ({ isOpen, onClose, invoice }) => {
  const theme = useTheme()
  const { patientId } = useParams()
  const { isMobile } = useDetectDevice()
  const { onOpenSnackbar } = useSnackbarContext()
  const { updateInvoiceDueDate, isUpdatingInvoiceDueDate } = useInvoiceActions({
    patientId,
  })

  const {
    control,
    setError,
    watch,
    formState: { errors, isSubmitting },
    handleSubmit: handleValidForm,
  } = useForm({
    defaultValues: {
      dueDate: invoice.dueDate ? parseISO(invoice.dueDate) : null,
    },
    resolver: yupResolver(schema),
  })

  const selectedDate = watch('dueDate')
  const showAwaitingProcessWarning =
    invoice.status === 'awaiting_process' &&
    selectedDate &&
    isToday(new Date(selectedDate))

  const handleSubmit = async data => {
    try {
      await updateInvoiceDueDate({
        invoiceId: invoice.id,
        paymentPlanId: invoice.paymentPlanId,
        dueDate: format(new Date(data.dueDate), 'yyyy-MM-dd'),
      })
      onClose()
      onOpenSnackbar('Due date updated.')
    } catch (error) {
      setError('root.updateInvoiceDueDate', {
        type: 'manual',
        message: error.message,
      })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      footer={<></>}
      title="Edit Due Date"
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
    >
      <Form onSubmit={handleValidForm(handleSubmit)}>
        <Controller
          name="dueDate"
          control={control}
          render={({ field }) => (
            <SelectCalendar
              isBasicCalendar
              label="Due date"
              labelFormat="MMM dd, yyyy"
              value={field.value}
              error={errors?.dueDate?.message}
              onChange={field.onChange}
            />
          )}
        />

        {showAwaitingProcessWarning && (
          <Box
            bgcolor={theme.palette.warning.light}
            color={theme.palette.warning.contrastText}
            p={1.5}
            display="flex"
            alignItems="flex-start"
            gap={1}
            borderRadius={1}
          >
            <WarningAmberIcon fontSize="small" />
            <Typography wordBreak fontSize="13px">
              This invoice is scheduled to be charged automatically. Saving
              today's date will trigger the next automatic charge attempt.
            </Typography>
          </Box>
        )}

        <FormErrorMessage errors={errors} name="root.updateInvoiceDueDate" />

        <Grid container gap={2} justifyContent="flex-end" mt={1}>
          <ActionButton
            variant="text"
            color="primary"
            onClick={onClose}
            disabled={isSubmitting || isUpdatingInvoiceDueDate}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant="contained"
            color="secondary"
            type="submit"
            disabled={isSubmitting || isUpdatingInvoiceDueDate}
            loading={isSubmitting || isUpdatingInvoiceDueDate}
          >
            Save
          </ActionButton>
        </Grid>
      </Form>
    </Modal>
  )
}

export default EditInvoiceDueDate
